import {
  AccessTimeOutlined,
  ErrorOutlineOutlined,
  FileDownloadOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Modal, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SucessModal from 'src/components/sucess-modal';
import { getBillingBatchRetrieve } from 'src/services/billingExportService/getBillingBatchRetrieve';
import { getBillingBlockers } from 'src/services/billingExportService/getBillingBlockers';
import { getPullBillingExport } from 'src/services/billingExportService/getPullBillingExport';
import { readBillingBlockers } from 'src/services/billingExportService/readBillingBlockers';
import {
  IBillingBatchRetrieveResponse,
  IBillingBlocker,
} from 'src/services/billingExportService/types';
import { CUSTOM } from 'src/sections/monthly-closeout-alert/customCloseout';
import { readStoredFacilityTimeZone } from 'src/utils/convertDateToSelectedTimeZone';
import moment from 'moment';
import 'moment-timezone';
import { useBillingExportRefresh } from './useBillingExportRefresh';

type Props = {
  // `location.state` from the alert view -- the closed period's date range.
  dateRange: any;
  /**
   * BOPS-440 — the blockers stopping the download, `[]` once there are none. `openList` is true
   * only when a download the user just asked for found blockers, so the list opens on its own.
   */
  onBlockersChange?: (blockers: IBillingBlocker[], options?: { openList: boolean }) => void;
};

//  BOPS-397 copy, word for word from the ticket.
export const GENERATING_COPY = 'Billing export is generating for this period.';
export const FAILED_COPY = "Billing export couldn't be generated. Please contact support.";
//  BOPS-440 — the blocker check could not run, so the download stays locked (not ticket copy).
export const BLOCKERS_UNAVAILABLE_COPY =
  "We couldn't check this billing export for billing blockers. Please refresh or contact support.";

/**
 * BOPS-440 — where the blocker check stands. Only `clear` lets the download through: `checking`
 * has no answer yet, and `unavailable` (a 503 or any other failure) never established that
 * nothing is blocking.
 */
type TBlockerCheck =
  | { status: 'checking' | 'clear' | 'unavailable'; blockers: [] }
  | { status: 'blocked'; blockers: IBillingBlocker[] };

const CHECKING: TBlockerCheck = { status: 'checking', blockers: [] };

/**
 * The archive name the service already chose, read from Content-Disposition. Axios lower-cases
 * response header names, and the header is only readable cross-origin because the service
 * explicitly exposes it -- hence the local fallback.
 */
const resolveFileName = (res: any, facilityName: string, month: number, year: number): string => {
  const disposition: string = res?.headers?.['content-disposition'] ?? '';
  const match = disposition.match(/filename=(?:"([^"]+)"|([^;]+))/i);
  const serverName = (match?.[1] ?? match?.[2])?.trim();

  return serverName || `${facilityName.trim()}_${month}_${year}_billing.zip`;
};

/**
 * A UTC refresh time in the facility's timezone, with the zone named, in the same shape as
 * billing-service's `refreshedAtLabel` and the invoice packet footers ("09/01/2026 01:05 PM CDT").
 * Only for times billing-service has not labelled itself (a period with no refresh on record reads
 * its time off the batch lookup). The zone billing-service reports wins; the facility's stored zone
 * is next; the browser's own zone is the last resort so the label is never missing.
 */
export const formatLastRefreshed = (utc: string, reportedZone?: string | null): string => {
  const zone = [reportedZone, readStoredFacilityTimeZone(), moment.tz.guess()].find(
    (candidate) => !!candidate && !!moment.tz.zone(candidate)
  );
  const instant = moment.utc(utc);
  if (!instant.isValid()) return '';
  return (zone ? instant.tz(zone) : instant.local()).format('MM/DD/YYYY hh:mm A z');
};

/**
 * "Download billing export" for a hard-closed period (BOPS-116).
 *
 * <p>BOPS-397: closing the closeout now starts a refresh of the export, and while billing-service
 * reports it GENERATING or FAILED the download stays disabled -- a file built before the close is the
 * thing being prevented. READY enables it without a reload. When billing-service has no refresh on
 * record for the period (it closed before refresh-on-close existed) or cannot answer, the screen
 * keeps its pre-BOPS-397 behaviour: the export is produced out-of-band by Dagster, the batch
 * lookup 404s until it exists, and the action stays disabled rather than offering a download that
 * would fail.
 */
const BillingExportButton = (props: Props) => {
  const { dateRange, onBlockersChange } = props;

  const [isBatchAvailable, setIsBatchAvailable] = useState<boolean>(false);
  const [isDateFilterSupported, setIsDateFilterSupported] = useState<boolean>(true);
  //  Raw UTC, formatted at render so it follows the zone billing-service reports.
  const [batchRefreshedAt, setBatchRefreshedAt] = useState<string>('');
  const [batchLoading, setBatchLoading] = useState<boolean>(true);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [downloadSuccess, setDownloadSuccess] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({ open: false, title: '', message: '' });
  //  BOPS-440 — what report-automation reports as stopping this facility's file (RA-2285).
  const [blockerCheck, setBlockerCheck] = useState<TBlockerCheck>(CHECKING);
  //  Held in a ref so a parent re-render handing over a new function does not re-notify.
  const onBlockersChangeRef = useRef(onBlockersChange);
  onBlockersChangeRef.current = onBlockersChange;

  const getFacilityUuid = localStorage.getItem('facilityId');
  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);

  const {
    view: refreshView,
    isKnown: isRefreshKnown,
    recheck: recheckRefresh,
  } = useBillingExportRefresh(dateRange?.startDate, dateRange?.endDate);
  const refreshStatus = refreshView?.status;
  const isRefreshing = refreshStatus === 'GENERATING';
  const isRefreshFailed = refreshStatus === 'FAILED';
  const isRefreshReady = refreshStatus === 'READY';

  //  The export is keyed by the month the period closes in.
  const periodStart = moment(dateRange?.startDate);
  const year = Number(periodStart.format('YYYY'));
  const month = Number(periodStart.format('M'));

  const facilityName =
    getAssignedFacilities?.response?.data?.data?.find((fac: any) => fac.uuid === getFacilityUuid)
      ?.name ?? '';

  //  Only a custom range needs the export narrowed to its dates; a monthly closeout covers the
  //  whole month the export already holds, so it stays available for every format.
  const isCustomRange = dateRange?.division === CUSTOM;
  const isBlockedByDateFilter = isCustomRange && !isDateFilterSupported;
  //  A custom range downloads just its own dates (BOPS-397: the export is per closeout period).
  //  start_date/end_date are only accepted where the format supports a date filter, and the
  //  action is already blocked for a custom range where it does not -- so a monthly closeout,
  //  which is its whole month, never sends them. The blocker check takes the same body.
  const rangeBody = isCustomRange
    ? {
        start_date: moment(dateRange?.startDate).format('YYYY-MM-DD'),
        end_date: moment(dateRange?.endDate).format('YYYY-MM-DD'),
      }
    : {};

  /**
   * BOPS-440 — ask report-automation what would block a pull of this period. Resolves to the
   * blockers (`[]` when clear) or `null` when the check could not run.
   */
  const checkBlockers = useCallback(async (): Promise<IBillingBlocker[] | null> => {
    try {
      const response = await getBillingBlockers({ enterprise_name: '', year, month, ...rangeBody });
      return readBillingBlockers(response?.data);
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, isCustomRange, dateRange?.startDate, dateRange?.endDate]);

  const applyBlockerCheck = (result: IBillingBlocker[] | null) => {
    if (result === null) setBlockerCheck({ status: 'unavailable', blockers: [] });
    else if (result.length) setBlockerCheck({ status: 'blocked', blockers: result });
    else setBlockerCheck({ status: 'clear', blockers: [] });
  };

  useEffect(() => {
    if (!periodStart.isValid()) {
      setBatchLoading(false);
      return;
    }
    //  Nothing to look up while a refresh is still building; READY re-runs this for the new file.
    //  The blockers belonged to the file being replaced, so they go with it.
    if (isRefreshing) {
      setBlockerCheck(CHECKING);
      return;
    }

    let isActive = true;
    setBatchLoading(true);
    setBlockerCheck(CHECKING);

    //  Separate from the batch lookup: a facility the export skipped has no file, and that is
    //  exactly when its blockers matter.
    checkBlockers().then((result) => {
      if (isActive) applyBlockerCheck(result);
    });

    getBillingBatchRetrieve({ enterprise_name: '', year, month })
      .then((response: { data: IBillingBatchRetrieveResponse }) => {
        if (!isActive) return;

        const billingFile = response?.data?.billing_files?.[0];
        if (!billingFile) {
          setIsBatchAvailable(false);
          setBatchRefreshedAt('');
          return;
        }

        //  Prefer the facility's own file refresh time; the batch-level `modified` covers the
        //  whole enterprise run and is only a fallback when this facility has no file entry.
        setBatchRefreshedAt(
          billingFile.last_refreshed ?? response?.data?.batch_information?.modified ?? ''
        );

        //  Absent means the service didn't report it, so leave the action enabled rather than
        //  blocking a download that would have worked.
        setIsDateFilterSupported(billingFile.date_filter_supported !== false);
        setIsBatchAvailable(true);
      })
      .catch(() => {
        //  Covers the not-generated-yet 404 and any transport failure alike: either way there is
        //  nothing to download, so the action stays disabled.
        if (!isActive) return;
        setIsBatchAvailable(false);
        setBatchRefreshedAt('');
      })
      .finally(() => {
        if (isActive) setBatchLoading(false);
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, isRefreshing, refreshView?.refreshedAt, checkBlockers]);

  const handleDownload = useCallback(async () => {
    setDownloadLoading(true);

    //  BOPS-440: check again right before pulling — blockers can appear after the screen loaded
    //  (a re-close), and only an answer of "none" is clear to download.
    const latest = await checkBlockers();
    applyBlockerCheck(latest);
    if (latest === null) {
      setDownloadLoading(false);
      return;
    }
    if (latest.length) {
      //  The user just asked, so open the list instead of only updating the banner.
      onBlockersChangeRef.current?.(latest, { openList: true });
      setDownloadLoading(false);
      return;
    }

    getPullBillingExport({
      enterprise_name: '',
      year,
      month,
      ...rangeBody,
    })
      .then((res: any) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        //  The service names the archive itself (facility + period); fall back to an equivalent
        //  local name if the header is ever stripped in transit.
        link.setAttribute('download', resolveFileName(res, facilityName, month, year));
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        //  A bundle can succeed but still be short of files. Only this header reports that -- the
        //  manifest naming them is inside the ZIP, which a browser cannot read without unzipping.
        const missingFiles = Number(res?.headers?.['x-billing-files-missing'] ?? 0);

        setDownloadSuccess(missingFiles === 0);
        setSnackbar({
          open: true,
          title: missingFiles > 0 ? 'Billing Export Incomplete' : 'Billing Export Downloaded!',
          message:
            missingFiles > 0
              ? `${facilityName} ${periodStart.format(
                  'MMMM YYYY'
                )} billing export downloaded, but ${missingFiles} file(s) are missing. See MANIFEST.txt inside the ZIP for details.`
              : `${facilityName} ${periodStart.format(
                  'MMMM YYYY'
                )} billing export successfully downloaded!`,
        });
      })
      .catch(async (error: any) => {
        //  BOPS-397: the server refuses the download while a refresh runs. That is the screen
        //  being out of date (a re-close since it loaded), not an error -- re-read the state so
        //  the button turns into the generating one instead of showing a failure.
        if (error?.response?.status === 409) {
          const latest = await recheckRefresh();
          if (latest?.status === 'GENERATING') return;
        }

        setDownloadSuccess(false);
        setSnackbar({
          open: true,
          title: 'Error Downloading Billing Export',
          message:
            'Something went wrong with downloading the billing export. Please refresh or let us know via Intercom if the issue persists.',
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, facilityName, isCustomRange, checkBlockers]);

  //  READY prints billing-service's own facility-zone label, never browser time. Formatting here
  //  is only for a READY that somehow arrived unlabelled, and for the batch lookup's time.
  const refreshedAt = (isRefreshReady && refreshView?.refreshedAt) || batchRefreshedAt;
  const lastRefreshed =
    (isRefreshReady && refreshView?.refreshedAtLabel) ||
    (refreshedAt ? formatLastRefreshed(refreshedAt, refreshView?.timeZone) : '');

  //  BOPS-440 — any blocker stops the download outright, READY or not; the banner above explains.
  const isBlocked = !isRefreshing && blockerCheck.status === 'blocked';
  const isBlockerCheckUnavailable = !isRefreshing && blockerCheck.status === 'unavailable';

  useEffect(() => {
    onBlockersChangeRef.current?.(isBlocked ? blockerCheck.blockers : []);
  }, [isBlocked, blockerCheck]);

  //  BOPS-463: READY only means the run finished, not that it produced a file.
  const isUnavailable = isRefreshing || isRefreshFailed || !isBatchAvailable;
  const isDisabled =
    !isRefreshKnown ||
    batchLoading ||
    isUnavailable ||
    blockerCheck.status !== 'clear' ||
    isBlockedByDateFilter ||
    downloadLoading;
  //  A running refresh shows its own status straight away; the rest wait for the batch lookup.
  //  A blocked export says so in the banner, which replaces the "couldn't be generated" line.
  const showStatus =
    isRefreshKnown && !isBlocked && (isRefreshing || isRefreshFailed || !batchLoading);

  const renderStatusText = () => {
    if (isRefreshing) return GENERATING_COPY;
    if (isRefreshFailed) return FAILED_COPY;
    if (isBlockerCheckUnavailable) return BLOCKERS_UNAVAILABLE_COPY;

    //  No file, whether or not a refresh ran: "Last refreshed" would offer a download that 404s.
    if (!isBatchAvailable) {
      return 'Billing export is not available for this period yet.';
    }

    //  "Refreshes hourly" is gone: the export now refreshes when a period closes (Nilesh, BOPS-397).
    //  The export itself is current even when the range can't be applied, so the refresh time
    //  stays alongside the reason the action is blocked.
    return [
      lastRefreshed && `Last refreshed ${lastRefreshed}`,
      isBlockedByDateFilter &&
        "This facility's billing export cannot be limited to a custom date range yet.",
    ]
      .filter(Boolean)
      .join(' · ');
  };

  return (
    <>
      <LoadingButton
        variant="contained"
        size="small"
        sx={{ whiteSpace: 'nowrap' }}
        startIcon={<FileDownloadOutlined />}
        loadingPosition="start"
        //  The spinner covers both a download in flight and a refresh still building.
        loading={downloadLoading || isRefreshing}
        disabled={isDisabled}
        onClick={handleDownload}
      >
        <Typography variant="buttonText">Download billing export</Typography>
      </LoadingButton>

      {showStatus && (
        <Stack
          data-slot="refresh-status"
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ order: { sm: 1 } }}
        >
          {isRefreshFailed || isBlockerCheckUnavailable ? (
            <ErrorOutlineOutlined
              fontSize="small"
              color="error"
            />
          ) : (
            <AccessTimeOutlined
              fontSize="small"
              color="disabled"
            />
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            role={
              isRefreshing || isRefreshFailed || isBlockerCheckUnavailable ? 'status' : undefined
            }
          >
            {renderStatusText()}
          </Typography>
        </Stack>
      )}

      <Modal open={snackbar.open}>
        <SucessModal
          close={() => setSnackbar({ ...snackbar, open: false })}
          title={snackbar.title}
          message={snackbar.message}
          isSucess={downloadSuccess}
        />
      </Modal>
    </>
  );
};

export default BillingExportButton;
